import React from "react";
import styles from "./alarm-node-details-panel.module.scss";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { TabButtons } from "@src/components/atoms/tab-buttons/tab-buttons";
import { AlarmTimeSeriesGraphWrapper } from "@src/components/molecules/alarm-time-series-graph-wrapper/alarm-time-series-graph-wrapper";
import nodeIconWhite from "@src/assets/svg/node-icon-white.svg";
import alarmProfileGray from "@src/assets/svg/alarm-profile-gray.svg";
import { AlarmList } from "@src/cloud-connect/alarms/entities/alarm-list";
import {
  ALARM_NODE_DETAILS_PANEL_TAB_VALUES,
  AlarmNodeDetailsPanelTabValue,
  useAlarmNodeDetailsPanelHook,
} from "./use-alarm-node-details-panel-hook";
import { TimestampsAndStringsDataTable } from "../node-details-panel/timestamps-and-strings-data-table/timestamps-and-strings-data-table";
import { useI18N } from "@src/language/language-context";

const cssClassName = "alarm-node-details-panel";

interface AlarmNodeDetailsPanelPropTypes {
  alarmListInstance: AlarmList;
}

export const AlarmNodeDetailsPanel = ({
  alarmListInstance,
}: AlarmNodeDetailsPanelPropTypes) => {
  const {
    activeProfile,
    graphOptionsObject,
    name,
    tabValue,
    setTabValue,
    stringDataAndTimestampData,
  } = useAlarmNodeDetailsPanelHook(alarmListInstance);

  const i18n = useI18N();

  const texts = {
    variableIconAlt: i18n("modules.alarmNodeDetailsPanel.variableIconAlt"),
    timeseriesLabel: i18n("modules.alarmNodeDetailsPanel.timeseriesLabel"),
    connectedTo: i18n("modules.alarmNodeDetailsPanel.connectedTo"),
    disclaimer: i18n("modules.alarmNodeDetailsPanel.disclaimer"),
    last8Hours: i18n("modules.alarmNodeDetailsPanel.last8Hours"),
    lastDay: i18n("modules.alarmNodeDetailsPanel.lastDay"),
    lastWeek: i18n("modules.alarmNodeDetailsPanel.lastWeek"),
    lastMonth: i18n("modules.alarmNodeDetailsPanel.lastMonth"),
  };

  const tabLabels: Record<AlarmNodeDetailsPanelTabValue, string> = {
    [ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_8_HOURS]: texts.last8Hours,
    [ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_DAY]: texts.lastDay,
    [ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_WEEK]: texts.lastWeek,
    [ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_MONTH]: texts.lastMonth,
  };

  const labelToTabValue: Record<string, AlarmNodeDetailsPanelTabValue> = {
    [texts.last8Hours]: ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_8_HOURS,
    [texts.lastDay]: ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_DAY,
    [texts.lastWeek]: ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_WEEK,
    [texts.lastMonth]: ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_MONTH,
  };

  const handleTabChange = (label: string) => {
    const nextTabValue = labelToTabValue[label];

    if (nextTabValue) {
      setTabValue(nextTabValue);
    }
  };

  const tabs = Object.values(tabLabels);

  return (
    <div className={styles[cssClassName]}>
      <FlexColumnContainer verticalGap={16}>
        <FlexColumnContainer>
          <div className={styles[`${cssClassName}__node-name-wrapper`]}>
            <img src={nodeIconWhite} alt={texts.variableIconAlt} />
            <span className={styles[`${cssClassName}__node-name`]}>{name}</span>
          </div>
          <div className={styles[`${cssClassName}__alarm-profile-wrapper`]}>
            <span className={styles[`${cssClassName}__alarm-profile-text`]}>
              {texts.connectedTo}
            </span>
            <img src={alarmProfileGray} alt={texts.variableIconAlt} />
            {activeProfile && (
              <span className={styles[`${cssClassName}__alarm-profile-text`]}>
                {activeProfile.getName()}
              </span>
            )}
          </div>
        </FlexColumnContainer>
        <FlexColumnContainer>
          <span className={styles[`${cssClassName}__label`]}>
            {texts.timeseriesLabel}
          </span>
          <TabButtons
            tabValue={tabLabels[tabValue]}
            callback={handleTabChange}
            tabs={tabs}
          />
          {activeProfile && !stringDataAndTimestampData && (
            <>
              <AlarmTimeSeriesGraphWrapper
                graphOptionsObject={graphOptionsObject}
              />
              <p className={styles[`${cssClassName}__disclaimer`]}>
                {texts.disclaimer}
              </p>
            </>
          )}
          {activeProfile && stringDataAndTimestampData && (
            <TimestampsAndStringsDataTable data={stringDataAndTimestampData} />
          )}
        </FlexColumnContainer>
      </FlexColumnContainer>
    </div>
  );
};
